import { useEffect, useMemo, useState } from 'react'
import { Link, useRouter, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Inbox,
  Plus,
  Search,
  Settings2,
  Target,
  Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useCreateGtmProjectMutation,
  useGtmProjectsQuery,
} from './gtm-workspace-queries'
import { resolveGtmProject, setLastGtmProjectId } from './last-gtm-project'
import { SidebarNavItem } from '@/components/molecules/sidebar/sidebar-nav-item'
import { SidebarTooltip } from '@/components/molecules/sidebar/sidebar-tooltip'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { GTM_PRODUCT_SCOPE } from '@/shared/lib/gtm-product-scope'
import { cn } from '@/shared/lib/utils'

const GTM_SECTIONS = [
  {
    to: '/$lang/workspace/$workspaceId/gtm',
    segment: '',
    icon: BarChart3,
    labelKey: 'ws_navAnalysis',
  },
  {
    to: '/$lang/workspace/$workspaceId/gtm/leads',
    segment: '/leads',
    icon: Users,
    labelKey: 'ws_navLeads',
  },
  {
    to: '/$lang/workspace/$workspaceId/gtm/inbox',
    segment: '/inbox',
    icon: Inbox,
    labelKey: 'ws_navInbox',
  },
  // Campaigns stays reachable by URL; it is folded into Leads in the next
  // iteration and is not a primary destination meanwhile.
  ...(GTM_PRODUCT_SCOPE.legacyWorkspaceUi
    ? [
        {
          to: '/$lang/workspace/$workspaceId/gtm/campaigns',
          segment: '/campaigns',
          icon: Target,
          labelKey: 'ws_navCampaigns',
        } as const,
      ]
    : []),
  {
    to: '/$lang/workspace/$workspaceId/gtm/settings',
    segment: '/settings',
    icon: Settings2,
    labelKey: 'ws_navSettings',
  },
] as const

function projectInitials(name: string | undefined): string {
  return (
    name
      ?.trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toLocaleUpperCase())
      .join('') || 'G'
  )
}

/** A project's mark: its initials on the accent tint, unlike workspaces. */
function ProjectMark({
  name,
  size = 'md',
}: {
  name: string | undefined
  size?: 'sm' | 'md'
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-accent font-semibold text-accent-foreground',
        size === 'md' ? 'size-7 text-[11px]' : 'size-5 text-[10px]',
      )}
      aria-hidden="true"
    >
      {projectInitials(name)}
    </span>
  )
}

/**
 * Active GTM project for the sidebar. The URL wins; otherwise the last project
 * opened in this workspace. Only GTM routes get `?project=` written back, so
 * Agents/Settings/Chats URLs stay clean.
 */
function useGtmNavProject(workspaceId: string) {
  const lang = useCurrentLang()
  const router = useRouter()
  const location = useRouterState({ select: (state) => state.location })
  const requestedProject = (location.search as { project?: string }).project
  const { data: projects = [] } = useGtmProjectsQuery(workspaceId)
  const base = `/${lang}/workspace/${workspaceId}/gtm`
  const onGtmRoute =
    location.pathname === base || location.pathname.startsWith(`${base}/`)
  const active = resolveGtmProject(
    projects,
    workspaceId,
    onGtmRoute ? requestedProject : undefined,
  )
  const activeId = active?.id

  useEffect(() => {
    if (!activeId || !onGtmRoute) return
    setLastGtmProjectId(workspaceId, activeId)
    if (requestedProject === activeId) return
    const params = new URLSearchParams(location.searchStr)
    params.set('project', activeId)
    void router.navigate({
      href: `${location.pathname}?${params.toString()}`,
      replace: true,
      resetScroll: false,
    })
  }, [
    activeId,
    location.pathname,
    location.searchStr,
    onGtmRoute,
    requestedProject,
    router,
    workspaceId,
  ])

  const selectProject = (projectId: string) => {
    setLastGtmProjectId(workspaceId, projectId)
    if (!onGtmRoute) {
      void router.navigate({
        to: '/$lang/workspace/$workspaceId/gtm',
        params: { lang, workspaceId },
        search: { project: projectId, step: 1 },
      })
      return
    }
    const params = new URLSearchParams(location.searchStr)
    params.set('project', projectId)
    params.delete('lead')
    void router.navigate({
      href: `${location.pathname}?${params.toString()}`,
      resetScroll: false,
    })
  }

  return {
    lang,
    base,
    pathname: location.pathname,
    projects,
    active,
    activeId,
    selectProject,
  }
}

type GtmNavProject = ReturnType<typeof useGtmNavProject>

function GtmProjectSelector({
  nav,
  workspaceId,
  collapsed,
}: {
  nav: GtmNavProject
  workspaceId: string
  collapsed: boolean
}) {
  const { t } = useTranslation('gtm')
  const [query, setQuery] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const [url, setUrl] = useState('')
  const createProject = useCreateGtmProjectMutation(workspaceId)
  const { projects, active, activeId, selectProject } = nav
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle
      ? projects.filter((item) =>
          `${item.companyName} ${item.domain}`.toLowerCase().includes(needle),
        )
      : projects
  }, [projects, query])
  const projectLabel = active?.companyName ?? t('ws_projectsEmpty')

  const trigger = collapsed ? (
    <button
      type="button"
      className="mx-auto flex size-9 items-center justify-center rounded-md outline-none transition-colors hover:bg-sidebar-accent/70 focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      aria-label={`${t('ws_project')}: ${projectLabel}`}
    >
      <ProjectMark name={active?.companyName} />
    </button>
  ) : (
    <button
      type="button"
      className="flex w-full min-w-0 items-center gap-2.5 rounded-md px-2 py-1.5 text-left outline-none transition-colors hover:bg-sidebar-accent/70 focus-visible:ring-2 focus-visible:ring-sidebar-ring data-[state=open]:bg-sidebar-accent/70"
      aria-label={`${t('ws_project')}: ${projectLabel}`}
    >
      <ProjectMark name={active?.companyName} />
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-medium uppercase leading-3 tracking-wide text-muted-foreground">
          {t('ws_project')}
        </span>
        <span className="block truncate text-sm font-medium leading-5">
          {projectLabel}
        </span>
      </span>
      <ChevronsUpDown
        className="size-3.5 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    </button>
  )

  return (
    <>
      <DropdownMenu onOpenChange={(open) => !open && setQuery('')}>
        {collapsed ? (
          <SidebarTooltip label={`${t('ws_project')}: ${projectLabel}`}>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </SidebarTooltip>
        ) : (
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          align="start"
          side={collapsed ? 'right' : 'bottom'}
          className="w-64"
        >
          {projects.length > 5 ? (
            <div
              className="relative p-1"
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Search className="absolute left-3 top-3 size-3.5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('ws_projectSearch')}
                className="h-8 pl-8 text-xs"
              />
            </div>
          ) : null}
          <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('ws_projects')}
          </DropdownMenuLabel>
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((project) => (
              <DropdownMenuItem
                key={project.id}
                onSelect={() => selectProject(project.id)}
              >
                <ProjectMark name={project.companyName} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{project.companyName}</span>
                  <span className="block truncate font-mono text-[11px] text-muted-foreground">
                    {project.domain}
                  </span>
                </span>
                {project.id === activeId ? <Check className="size-4" /> : null}
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setNewOpen(true)}>
            <Plus className="size-4" />
            {t('ws_newProject')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ws_newProject')}</DialogTitle>
            <DialogDescription>
              {t('ws_newProjectDescription')}
            </DialogDescription>
          </DialogHeader>
          <label className="grid gap-1.5 text-sm font-medium">
            {t('urlLabel')}
            <Input
              value={url}
              placeholder="example.com"
              onChange={(event) => setUrl(event.target.value)}
              autoFocus
            />
          </label>
          {createProject.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t('ws_newProjectError')}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              disabled={!url.trim() || createProject.isPending}
              onClick={() =>
                createProject.mutate(url.trim(), {
                  onSuccess: ({ project }) => {
                    setNewOpen(false)
                    setUrl('')
                    selectProject(project.id)
                  },
                })
              }
            >
              {t('ws_startProjectAnalysis')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function GtmMark() {
  return (
    <span
      className="flex size-5 shrink-0 items-center justify-center rounded bg-primary text-[10px] font-bold text-primary-foreground"
      aria-hidden="true"
    >
      G
    </span>
  )
}

/**
 * The GTM employee: product heading, project selector and project-scoped
 * sections. `collapsible` turns the heading into an accordion when the
 * workspace has other employees; `collapsed` is the icon-only sidebar.
 */
export function GtmWorkspaceNav({
  workspaceId,
  collapsible = true,
  collapsed = false,
}: {
  workspaceId: string
  collapsible?: boolean
  collapsed?: boolean
}) {
  const { t } = useTranslation('gtm')
  const nav = useGtmNavProject(workspaceId)
  const [expanded, setExpanded] = useState(true)
  const { lang, base, pathname, activeId } = nav
  const showSections = collapsed || !collapsible || expanded

  const sections = GTM_SECTIONS.map((section) => {
    const path = `${base}${section.segment}`
    const selected =
      section.segment === ''
        ? pathname === path || pathname === `${path}/`
        : pathname.startsWith(path)
    return (
      <SidebarNavItem
        key={section.to}
        icon={section.icon}
        label={t(section.labelKey)}
        active={selected}
        collapsed={collapsed}
      >
        <Link
          to={section.to}
          params={{ lang, workspaceId }}
          search={{ project: activeId }}
          activeOptions={{
            exact: section.segment === '',
            includeSearch: false,
          }}
        />
      </SidebarNavItem>
    )
  })

  if (collapsed) {
    return (
      <div className="grid gap-1" aria-label="GTM">
        <GtmProjectSelector nav={nav} workspaceId={workspaceId} collapsed />
        {sections}
      </div>
    )
  }

  return (
    <div className="grid gap-1">
      {collapsible ? (
        <button
          type="button"
          aria-label="GTM"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className="flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-sm font-semibold outline-none transition-colors hover:bg-sidebar-accent/70 focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <GtmMark />
          <span className="min-w-0 flex-1 text-left">GTM</span>
          <ChevronDown
            className={cn(
              'size-3.5 text-muted-foreground transition-transform',
              !expanded && '-rotate-90',
            )}
            aria-hidden="true"
          />
        </button>
      ) : null}
      {showSections ? (
        <div
          className={cn('grid gap-1', collapsible && 'ml-3 border-l pl-2')}
          aria-label="GTM navigation"
        >
          <div className="pb-1">
            <GtmProjectSelector
              nav={nav}
              workspaceId={workspaceId}
              collapsed={false}
            />
          </div>
          {sections}
        </div>
      ) : null}
    </div>
  )
}
